import { useCallback, useEffect, useState } from "react";
import { getQrPayments } from "../../services";
import { useUserRole } from "./useUserRole";
import { featureAccess } from "../../permissions/access";

/**
 * How often the count is refreshed while a screen is open.
 *
 * A minute, because the thing being counted is time-critical in a way most badges are not:
 * SCB will only reverse a duplicate payment on the Bangkok day it arrived, so the window to
 * act on one closes at midnight and every minute of delay is a minute of it spent. Accounting
 * keep this app open all day, which is exactly why the count belongs here rather than in a
 * chat message someone may or may not read.
 */
const REFRESH_MS = 60_000;

/**
 * How many QR codes are currently holding more than one payment.
 *
 * Counted server-side: the list endpoint answers with `count` for the whole filter, so asking
 * for a single row is enough and the badge never depends on what page anyone is looking at.
 *
 * Deliberately counts ONLY duplicates, not everything on the QR Transaction screen. The
 * unclaimed and paid-after-cancel queues are real work too, but they carry a standing backlog
 * and no deadline — folding them in would leave a badge permanently showing some number, which
 * is a badge nobody reads. This one is zero almost always, and when it is not, something has to
 * happen today.
 *
 * Returns 0 for anyone who cannot open the screen, and never throws: a failed poll leaves the
 * previous count in place rather than blanking a warning that may still be true.
 */
export function useQrDuplicateCount(): number {
  const role = useUserRole();
  const allowed = featureAccess.qrTransaction(role);
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!allowed) return;
    try {
      const {
        data: { data },
      } = await getQrPayments({ status: "DUPLICATE_PAYMENT", limit: 1 });
      setCount(Number(data?.count ?? 0));
    } catch {
      // Swallowed on purpose. This is an ambient indicator on every page; a toast or a thrown
      // error from it would interrupt whatever the user is actually doing, to report that a
      // badge could not be refreshed.
    }
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      setCount(0);
      return;
    }
    fetchCount();
    const timer = setInterval(fetchCount, REFRESH_MS);
    // Also on return to the tab: someone coming back from the bank's own screen should not
    // wait out the rest of an interval to see what changed while they were away.
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [allowed, fetchCount]);

  return allowed ? count : 0;
}
